import { AccessControlRequestTemplate } from '@agilelab/plugin-wb-builder-common';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import { catalogApiRef } from '@mainstage/plugin-catalog-react';
import useAsync from 'react-use/lib/useAsync';

export type AccessControlRequestTemplateResponse = {
  template: AccessControlRequestTemplate | undefined;
  allTemplates: AccessControlRequestTemplate[];
  error: string | undefined;
};

function entityRef(t: AccessControlRequestTemplate): string {
  return `${t.metadata.namespace ?? 'default'}/${t.metadata.name}`;
}

function toIdArray(v: string | string[] | undefined): string[] {
  if (v === undefined) return [];
  return Array.isArray(v) ? v : [v];
}

/**
 * This hook is responsible of taking the current Access Control Request template from the catalog API.
 *
 * When `useCaseTemplateIdHierarchy` is provided (ordered list, most specific first), the hook
 * fetches all templates for the given `spec.type` and splits them into:
 * 1. Specific templates whose `spec.useCaseTemplateId` array contains any ID from the hierarchy
 * 2. Default templates (no `spec.useCaseTemplateId` or empty array)
 * Results are merged: specific templates first (preserving hierarchy order), then defaults.
 *
 * When `useCaseTemplateIdHierarchy` is not provided or empty, all templates for the given
 * `spec.type` are fetched (backward-compatible behavior).
 *
 * `allTemplates` always contains the full list of fetched templates for the given `spec.type`.
 * @returns an AccessRequest template or undefined
 */
export function useAccessControlRequestTemplate(options: {
  templateType: 'grant' | 'revoke';
  useCaseTemplateIdHierarchy?: string[];
}): AccessControlRequestTemplateResponse {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);

  const hierarchyKey = options.useCaseTemplateIdHierarchy?.join(',') ?? '';

  const { value: accessControlRequestTemplates, error } = useAsync(async () => {
    const token = (await identityApi.getCredentials()).token;
    const hasHierarchy = hierarchyKey.length > 0;

    if (!hasHierarchy) {
      return (
        await catalogApi.getEntities(
          {
            filter: {
              kind: 'AccessControlRequestTemplate',
              'spec.type': options.templateType,
            },
          },
          { token },
        )
      ).items as AccessControlRequestTemplate[];
    }

    const hierarchy = options.useCaseTemplateIdHierarchy!;
    const hierarchySet = new Set(hierarchy);

    const allItems = (
      await catalogApi.getEntities(
        {
          filter: {
            kind: 'AccessControlRequestTemplate',
            'spec.type': options.templateType,
          },
        },
        { token },
      )
    ).items as AccessControlRequestTemplate[];

    const specificTemplates = allItems.filter(t =>
      toIdArray(t.spec.useCaseTemplateId).some(id => hierarchySet.has(id)),
    );
    const specificRefs = new Set(specificTemplates.map(entityRef));

    const defaultTemplates = allItems.filter(
      t =>
        toIdArray(t.spec.useCaseTemplateId).length === 0 &&
        !specificRefs.has(entityRef(t)),
    );

    return [...specificTemplates, ...defaultTemplates];
  }, [catalogApi, identityApi, options.templateType, hierarchyKey]);

  if (error) {
    return {
      template: undefined,
      allTemplates: [],
      error: `Error while trying to fetch the current Access Control Request template: ${error.message}`,
    };
  }

  if (!accessControlRequestTemplates) {
    return {
      template: undefined,
      allTemplates: [],
      error: `Something went wrong while trying to fetch the current Access Control Request template.`,
    };
  }

  if (accessControlRequestTemplates.length < 1) {
    return {
      template: undefined,
      allTemplates: [],
      error:
        'There is no registered access control request template. Please, contact the platform team.',
    };
  }

  const templates = accessControlRequestTemplates;

  return {
    template: templates[0],
    allTemplates: templates,
    error: undefined,
  };
}
